/* eslint-disable*/
import React, { useEffect, useState, useRef, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSearch,
  faHome,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import moment from "moment";
import { addMonths, subMonths, startOfMonth } from "date-fns";
import { showToast } from "../../components/toast";
import '../../components/skeletonRow.css'

const SubscriptionAnalysisReport = () => {
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [subscriptionAmount, setSubscriptionAmount] = useState({ amount: 0 });
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const tableRenderedRef = useRef(false);
  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));

  // Separate state for form inputs and actual filters - using Date objects for date ranges
  const [formInputs, setFormInputs] = useState({
    created_on_start: null,
    created_on_end: null,
    lead_source: "",
    serviceid: "",
    stype: "",
    status: "",
    servicetype: "",
    amount: "",
    activation_date_start: null,
    activation_date_end: null,
    expiry_date_start: null,
    expiry_date_end: null,
    pay_date_start: null,
    pay_date_end: null,
    trades_total: "",
    trades_used: "",
    trades_available: "",
  });

  const [filters, setFilters] = useState({
    created_on_start: "",
    created_on_end: "",
    lead_source: "",
    serviceid: "",
    stype: "",
    status: "",
    servicetype: "",
    amount: "",
    activation_date_start: "",
    activation_date_end: "",
    expiry_date_start: "",
    expiry_date_end: "",
    pay_date_start: "",
    pay_date_end: "",
    trades_total: "",
    trades_used: "",
    trades_available: "",
    orderbycol: "created_on",
    orderby: "desc",
    page: 1,
    limit: 100,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    showingRecords: "Showing 0 to 0 of 0 entries",
  });

  const [dropdownOptions, setDropdownOptions] = useState({
    leadSourceList: [],
    serviceList: [],
    subscriptionTypeList: [],
    statusList: [],
    serviceTypeList: [],
    planTypeList: [],
  });

  // Refs for double scroll
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  // Transform API response to dropdown format
  const transformDropdownData = (apiData) => {
    const serviceList = (apiData.servicelist || []).map((service) => ({
      value: service.id,
      label: service.service_name,
    }));

    const leadSourceList = (apiData.lead_sourcelist || []).map((source) => ({
      value: source.pick_name,
      label: source.pick_name,
    }));

    const statusList = (apiData.statuslist || []).map((status) => ({
      value: status.pick_name,
      label: status.pick_name,
    }));

    const subscriptionTypeList = (apiData.subscription_typelist || []).map(
      (type) => ({
        value: type.pick_name,
        label: type.pick_name,
      })
    );

    const serviceTypeList = (apiData.service_typelist || []).map((type) => ({
      value: type.pick_name,
      label: type.pick_name,
    }));

    const planTypeList = (apiData.plan_typelist || []).map((type) => ({
      value: type.pick_name,
      label: type.pick_name,
    }));

    return {
      serviceList,
      leadSourceList,
      statusList,
      subscriptionTypeList,
      serviceTypeList,
      planTypeList,
    };
  };

  // Fetch subscription data
  const fetchSubscriptionData = useCallback(
    async (pageNumber = 1) => {
      try {
        setLoading(true);
        setIsRendering(true);
        tableRenderedRef.current = false;

        const token = localStorage.getItem("authToken");

        const filterParams = {
          page: pageNumber - 1, // Backend expects 0-based page
          limit: filters.limit,
          orderbycol: filters.orderbycol,
          orderby: filters.orderby.toUpperCase(), // Backend expects uppercase
        };

        // Apply filters - only add to params if they have values
        if (filters.lead_source) filterParams.lead_source = filters.lead_source;
        if (filters.serviceid) filterParams.serviceid = filters.serviceid;
        if (filters.stype) filterParams.stype = filters.stype;
        if (filters.status) filterParams.status = filters.status;
        if (filters.servicetype) filterParams.servicetype = filters.servicetype;
        if (filters.amount) filterParams.amount = filters.amount;
        if (filters.trades_total)
          filterParams.trades_total = filters.trades_total;
        if (filters.trades_used) filterParams.trades_used = filters.trades_used;
        if (filters.trades_available)
          filterParams.trades_available = filters.trades_available;

        // Date range filters - format as YYYY-MM-DD with proper parameter names
        if (filters.created_on_start) {
          filterParams.screated_on = moment(filters.created_on_start).format(
            "YYYY-MM-DD"
          );
        }
        if (filters.created_on_end) {
          filterParams.ecreated_on = moment(filters.created_on_end).format(
            "YYYY-MM-DD"
          );
        }
        if (filters.activation_date_start) {
          filterParams.sactivation_date = moment(
            filters.activation_date_start
          ).format("YYYY-MM-DD");
        }
        if (filters.activation_date_end) {
          filterParams.eactivation_date = moment(
            filters.activation_date_end
          ).format("YYYY-MM-DD");
        }
        if (filters.expiry_date_start) {
          filterParams.sexpiry_date = moment(filters.expiry_date_start).format(
            "YYYY-MM-DD"
          );
        }
        if (filters.expiry_date_end) {
          filterParams.eexpiry_date = moment(filters.expiry_date_end).format(
            "YYYY-MM-DD"
          );
        }
        if (filters.pay_date_start) {
          filterParams.spay_date = moment(filters.pay_date_start).format(
            "YYYY-MM-DD"
          );
        }
        if (filters.pay_date_end) {
          filterParams.epay_date = moment(filters.pay_date_end).format(
            "YYYY-MM-DD"
          );
        }

        // Add formatted date ranges for display (optional)
        if (filters.created_on_start && filters.created_on_end) {
          filterParams.created_on = `${moment(filters.created_on_start).format(
            "DD-MM-YYYY"
          )} - ${moment(filters.created_on_end).format("DD-MM-YYYY")}`;
        }
        if (filters.activation_date_start && filters.activation_date_end) {
          filterParams.activation_date = `${moment(
            filters.activation_date_start
          ).format("DD-MM-YYYY")} - ${moment(
            filters.activation_date_end
          ).format("DD-MM-YYYY")}`;
        }
        if (filters.expiry_date_start && filters.expiry_date_end) {
          filterParams.expiry_date = `${moment(
            filters.expiry_date_start
          ).format("DD-MM-YYYY")} - ${moment(filters.expiry_date_end).format(
            "DD-MM-YYYY"
          )}`;
        }
        if (filters.pay_date_start && filters.pay_date_end) {
          filterParams.pay_date = `${moment(filters.pay_date_start).format(
            "DD-MM-YYYY"
          )} - ${moment(filters.pay_date_end).format("DD-MM-YYYY")}`;
        }

        console.log("API Payload:", filterParams); // Debug log

        const response = await axios.post(
          `${API_URL}report/subscription-analysis`,
          filterParams,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "*/*",
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data) {
          // Set subscription data
          setSubscriptionData(response.data.subscription || []);

          // Set subscription amount for footer
          if (response.data.subscriptionamount) {
            setSubscriptionAmount(response.data.subscriptionamount);
          }

          // Set dropdown options from response
          const transformedDropdowns = transformDropdownData(response.data);
          setDropdownOptions(transformedDropdowns);

          // Set pagination
          const totalRecords =
            response.data.totalRecords ||
            response.data.subscription?.length ||
            0;
          const totalPages = Math.ceil(totalRecords / filters.limit);
          const startRecord = (pageNumber - 1) * filters.limit + 1;
          const endRecord = Math.min(pageNumber * filters.limit, totalRecords);

          setPagination({
            currentPage: pageNumber,
            totalPages: totalPages,
            totalRecords: totalRecords,
            showingRecords: `${startRecord} to ${endRecord} of ${totalRecords}`,
          });

          setTimeout(() => {
            tableRenderedRef.current = true;
            checkRenderingStatus();
          }, 100);
        }
      } catch (error) {
        console.error("Error fetching subscription data:", error);
        console.error("Error details:", error.response?.data); // Debug log
        setSubscriptionData([]);
        tableRenderedRef.current = true;
        checkRenderingStatus();
        showToast.error("Failed to fetch subscription data");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  const checkRenderingStatus = () => {
    if (tableRenderedRef.current) {
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    }
  };

  useEffect(() => {
    fetchSubscriptionData(filters.page);
  }, [filters, fetchSubscriptionData]);

  // Double scroll implementation
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };

    updateScrollbar();
    window.addEventListener("resize", updateScrollbar);
    return () => window.removeEventListener("resize", updateScrollbar);
  }, [subscriptionData]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  // Handle form input changes (without applying filters immediately)
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle dropdown changes with immediate filter application
  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    setFormInputs((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Apply filters immediately for dropdown changes
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };

  // Handle date range changes for Created Date
  const handleCreatedDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs((prev) => ({
      ...prev,
      created_on_start: start,
      created_on_end: end,
    }));
  };

  // Handle date range changes for Activation Date
  const handleActivationDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs((prev) => ({
      ...prev,
      activation_date_start: start,
      activation_date_end: end,
    }));
  };

  // Handle date range changes for Expiry Date
  const handleExpiryDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs((prev) => ({
      ...prev,
      expiry_date_start: start,
      expiry_date_end: end,
    }));
  };

  // Handle date range changes for Pay Date
  const handlePayDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs((prev) => ({
      ...prev,
      pay_date_start: start,
      pay_date_end: end,
    }));
  };

  // Apply all filters (called on Enter key or search button click)
  const applyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      lead_source: formInputs.lead_source,
      serviceid: formInputs.serviceid,
      stype: formInputs.stype,
      status: formInputs.status,
      servicetype: formInputs.servicetype,
      amount: formInputs.amount,
      trades_total: formInputs.trades_total,
      trades_used: formInputs.trades_used,
      trades_available: formInputs.trades_available,
      created_on_start: formInputs.created_on_start,
      created_on_end: formInputs.created_on_end,
      activation_date_start: formInputs.activation_date_start,
      activation_date_end: formInputs.activation_date_end,
      expiry_date_start: formInputs.expiry_date_start,
      expiry_date_end: formInputs.expiry_date_end,
      pay_date_start: formInputs.pay_date_start,
      pay_date_end: formInputs.pay_date_end,
      page: 1,
    }));
  };

  // Apply date filters for specific date ranges
  const applyDateFilter = (dateField) => {
    setFilters((prev) => ({
      ...prev,
      [dateField + "_start"]: formInputs[dateField + "_start"],
      [dateField + "_end"]: formInputs[dateField + "_end"],
      page: 1,
    }));
  };

  // Handle Enter key in text inputs
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const handleSort = (column) => {
    setFilters((prev) => ({
      ...prev,
      orderbycol: column,
      orderby:
        prev.orderbycol === column && prev.orderby === "asc" ? "desc" : "asc",
      page: 1,
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  const exportReport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("authToken");

      const filterParams = {};

      // Apply same filters as fetch
      if (filters.lead_source) filterParams.lead_source = filters.lead_source;
      if (filters.serviceid) filterParams.serviceid = filters.serviceid;
      if (filters.stype) filterParams.stype = filters.stype;
      if (filters.status) filterParams.status = filters.status;
      if (filters.servicetype) filterParams.servicetype = filters.servicetype;
      if (filters.amount) filterParams.amount = filters.amount;
      if (filters.trades_total)
        filterParams.trades_total = filters.trades_total;
      if (filters.trades_used) filterParams.trades_used = filters.trades_used;
      if (filters.trades_available)
        filterParams.trades_available = filters.trades_available;

      // Date range filters for export - using same parameter names as API expects
      if (filters.created_on_start) {
        filterParams.screated_on = moment(filters.created_on_start).format(
          "YYYY-MM-DD"
        );
      }
      if (filters.created_on_end) {
        filterParams.ecreated_on = moment(filters.created_on_end).format(
          "YYYY-MM-DD"
        );
      }
      if (filters.activation_date_start) {
        filterParams.sactivation_date = moment(
          filters.activation_date_start
        ).format("YYYY-MM-DD");
      }
      if (filters.activation_date_end) {
        filterParams.eactivation_date = moment(
          filters.activation_date_end
        ).format("YYYY-MM-DD");
      }
      if (filters.expiry_date_start) {
        filterParams.sexpiry_date = moment(filters.expiry_date_start).format(
          "YYYY-MM-DD"
        );
      }
      if (filters.expiry_date_end) {
        filterParams.eexpiry_date = moment(filters.expiry_date_end).format(
          "YYYY-MM-DD"
        );
      }
      if (filters.pay_date_start) {
        filterParams.spay_date = moment(filters.pay_date_start).format(
          "YYYY-MM-DD"
        );
      }
      if (filters.pay_date_end) {
        filterParams.epay_date = moment(filters.pay_date_end).format(
          "YYYY-MM-DD"
        );
      }

      // Add order parameters
      filterParams.orderbycol = filters.orderbycol;
      filterParams.orderby = filters.orderby.toUpperCase();

      console.log("Export Payload:", filterParams); // Debug log

      const response = await axios.post(
        `${API_URL}report/subscription-analysis/export`,
        filterParams,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `subscription-analysis-report-${new Date().toISOString().split("T")[0]
        }.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success("Report exported successfully.");
    } catch (error) {
      console.error("Error exporting report:", error);
      showToast.error("Failed to export report. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const clearFilters = () => {
    setFormInputs({
      created_on_start: null,
      created_on_end: null,
      lead_source: "",
      serviceid: "",
      stype: "",
      status: "",
      servicetype: "",
      amount: "",
      activation_date_start: null,
      activation_date_end: null,
      expiry_date_start: null,
      expiry_date_end: null,
      pay_date_start: null,
      pay_date_end: null,
      trades_total: "",
      trades_used: "",
      trades_available: "",
    });
    setFilters({
      created_on_start: "",
      created_on_end: "",
      lead_source: "",
      serviceid: "",
      stype: "",
      status: "",
      servicetype: "",
      amount: "",
      activation_date_start: "",
      activation_date_end: "",
      expiry_date_start: "",
      expiry_date_end: "",
      pay_date_start: "",
      pay_date_end: "",
      trades_total: "",
      trades_used: "",
      trades_available: "",
      orderbycol: "created_on",
      orderby: "desc",
      page: 1,
      limit: 100,
    });
  };

  useEffect(() => {
    const handleClearFilters = () => {
      clearFilters();
    };
    window.addEventListener('clearSubscriptionAnalysisReportFiltersEvent', handleClearFilters);
    return () => {
      window.removeEventListener('clearSubscriptionAnalysisReportFiltersEvent', handleClearFilters);
    };
  }, []);

  // Helper functions
  const getSortIcon = (column) => {
    if (filters.orderbycol !== column) return faSort;
    return filters.orderby === "asc" ? faSortUp : faSortDown;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return moment(date).format("DD-MM-YYYY HH:mm A");
    } catch (error) {
      return "";
    }
  };

  const formatDateWithTime = (dateString, timeString) => {
    if (!dateString || dateString === "0000-00-00") return "";
    try {
      const date = moment(dateString, "YYYY-MM-DD");
      if (!date.isValid()) return "";

      if (timeString && timeString !== "00:00:00") {
        return `${date.format("DD-MM-YYYY")} ${timeString}`;
      }
      return date.format("DD-MM-YYYY");
    } catch (error) {
      return "";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return moment(date).format("DD-MM-YYYY");
    } catch (error) {
      return "";
    }
  };

  const hasAnyFilter = () => {
    return (
      filters.lead_source ||
      filters.serviceid ||
      filters.stype ||
      filters.status ||
      filters.servicetype ||
      filters.amount ||
      filters.trades_total ||
      filters.trades_used ||
      filters.trades_available ||
      filters.created_on_start ||
      filters.created_on_end ||
      filters.activation_date_start ||
      filters.activation_date_end ||
      filters.expiry_date_start ||
      filters.expiry_date_end ||
      filters.pay_date_start ||
      filters.pay_date_end ||
      filters.orderbycol !== "created_on" ||
      filters.orderby !== "desc"
    );
  };

  const isPrevDisabled = pagination.currentPage <= 1;
  const isNextDisabled = pagination.currentPage >= pagination.totalPages;

  return (
    <div className="panel-content mt-5">
      {/* {(loading || isRendering) && <LoadingSpinner />} */}

      {/* Header with Breadcrumb and Controls */}
      <div className="panel-heading d-flex justify-content-between">
        <div className="col-md-4">
          <div
            style={{ float: "left", position: "relative", marginLeft: "5px" }}
          >
            {hasAnyFilter() && (
              <button
                className="btn btn-default btn-xs ml-1"
                style={{ marginLeft: "6px" }}
                onClick={clearFilters}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
        <div>
          <span style={{ fontSize: "0.8rem" }} className="me-3">
            {loading ? (
              <span
                className="skeleton-text"
                style={{ width: "140px", display: "inline-block" }}
              ></span>
            ) : (
              pagination.showingRecords
            )}
          </span>

          <button
            className="btn btn-default btn-sm mx-1"
            onClick={() => fetchSubscriptionData(1)}
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={exportReport}
            disabled={loading || subscriptionData.length === 0}
          >
            <FontAwesomeIcon icon={faDownload} /> Export Report
          </button>
          <button
            className={`btn btn-default btn-sm ${filters.page === 1 ? "d-none" : ""
              }`}
            title="Previous"
            onClick={() => handlePageChange(filters.page - 1)}
            disabled={filters.page === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          &nbsp;
          <button
            className={`btn btn-default btn-sm ${filters.page === pagination.totalPages ? "d-none" : ""
              }`}
            title="Next"
            onClick={() => handlePageChange(filters.page + 1)}
            disabled={filters.page === pagination.totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* Main Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="large-table-container-3">
          {/* Top Scrollbar */}
          <div
            ref={topScrollbarRef}
            onScroll={handleTopScroll}
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              height: "16px",
              marginBottom: "2px",
              background: "#f8f9fa",
            }}
          >
            <div ref={topScrollbarContentRef} style={{ height: "1px" }}></div>
          </div>

          {/* Table Container */}
          <div
            ref={tableContainerRef}
            onScroll={syncScroll}
            style={{
              overflowY: "auto",
              height: "calc(112vh - 250px)",
              overflowX: "auto",
              position: "relative",
            }}
          >
            <table className="table table-condensed table-hover bg-white sticky-table">
              <thead>
                {/* Header Row */}
                <tr style={{ cursor: "pointer" }}>
                  <th onClick={() => handleSort("created_on")}>
                    Created Time{" "}
                    <FontAwesomeIcon icon={getSortIcon("created_on")} />
                  </th>
                  <th>Lead Source</th>
                  <th onClick={() => handleSort("serviceid")}>
                    Service <FontAwesomeIcon icon={getSortIcon("serviceid")} />
                  </th>
                  <th onClick={() => handleSort("stype")}>
                    Type <FontAwesomeIcon icon={getSortIcon("stype")} />
                  </th>
                  <th onClick={() => handleSort("status")}>
                    Status <FontAwesomeIcon icon={getSortIcon("status")} />
                  </th>
                  <th onClick={() => handleSort("servicetype")}>
                    Service Type{" "}
                    <FontAwesomeIcon icon={getSortIcon("servicetype")} />
                  </th>
                  <th onClick={() => handleSort("amount")}>
                    Amount <FontAwesomeIcon icon={getSortIcon("amount")} />
                  </th>
                  <th onClick={() => handleSort("activation_date")}>
                    Activation Date{" "}
                    <FontAwesomeIcon icon={getSortIcon("activation_date")} />
                  </th>
                  <th onClick={() => handleSort("expiry_date")}>
                    Expiry Date{" "}
                    <FontAwesomeIcon icon={getSortIcon("expiry_date")} />
                  </th>
                  <th onClick={() => handleSort("pay_date")}>
                    Pay Date <FontAwesomeIcon icon={getSortIcon("pay_date")} />
                  </th>
                  <th onClick={() => handleSort("trades_total")}>
                    Total Credits{" "}
                    <FontAwesomeIcon icon={getSortIcon("trades_total")} />
                  </th>
                  <th onClick={() => handleSort("trades_used")}>
                    Total Used{" "}
                    <FontAwesomeIcon icon={getSortIcon("trades_used")} />
                  </th>
                  <th onClick={() => handleSort("trades_available")}>
                    Total Available{" "}
                    <FontAwesomeIcon icon={getSortIcon("trades_available")} />
                  </th>
                </tr>

                {/* Filter Row */}
                <tr>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <DatePicker
                        selected={formInputs.created_on_start}
                        onChange={handleCreatedDateChange}
                        startDate={formInputs.created_on_start}
                        endDate={formInputs.created_on_end}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText=""
                        monthsShown={3}
                        openToDate={
                          formInputs.created_on_start || new Date()}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                        minDate={new Date(2018, 0, 1)}
                        maxDate={new Date(2030, 11, 31)}
                        dateFormat="dd-MM-yyyy"
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => applyDateFilter("created_on")}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th>
                    <select
                      name="lead_source"
                      value={formInputs.lead_source}
                      onChange={handleDropdownChange}
                      style={{ width: "100px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.leadSourceList.map((item, index) => (
                        <option key={index} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <select
                      name="serviceid"
                      value={formInputs.serviceid}
                      onChange={handleDropdownChange}
                      style={{ width: "150px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.serviceList.map((item, index) => (
                        <option key={index} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <select
                      name="stype"
                      value={formInputs.stype}
                      onChange={handleDropdownChange}
                      style={{ width: "80px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.subscriptionTypeList.map(
                        (item, index) => (
                          <option key={index} value={item.value}>
                            {item.label}
                          </option>
                        )
                      )}
                    </select>
                  </th>
                  <th>
                    <select
                      name="status"
                      value={formInputs.status}
                      onChange={handleDropdownChange}
                      style={{ width: "80px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.statusList.map((item, index) => (
                        <option key={index} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <select
                      name="servicetype"
                      value={formInputs.servicetype}
                      onChange={handleDropdownChange}
                      style={{ width: "80px", padding: "5px" }}
                    >
                      <option value="">--select--</option>
                      {dropdownOptions.serviceTypeList.map((item, index) => (
                        <option key={index} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <input
                      type="text"
                      name="amount"
                      placeholder="Amount"
                      value={formInputs.amount}
                      onChange={handleFormInputChange}
                      onKeyDown={handleKeyDown}
                      style={{ width: "80px", padding: "5px" }}
                    />
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <DatePicker
                        selected={formInputs.activation_date_start}
                        onChange={handleActivationDateChange}
                        startDate={formInputs.activation_date_start}
                        endDate={formInputs.activation_date_end}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText=""
                        monthsShown={3}
                        openToDate={
                          formInputs.activation_date_start || new Date()}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                        minDate={new Date(2018, 0, 1)}
                        maxDate={new Date(2030, 11, 31)}
                        dateFormat="dd-MM-yyyy"
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => applyDateFilter("activation_date")}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <DatePicker
                        selected={formInputs.expiry_date_start}
                        onChange={handleExpiryDateChange}
                        startDate={formInputs.expiry_date_start}
                        endDate={formInputs.expiry_date_end}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText=""
                        monthsShown={3}
                        openToDate={
                          formInputs.expiry_date_start || new Date()}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                        minDate={new Date(2018, 0, 1)}
                        maxDate={new Date(2030, 11, 31)}
                        dateFormat="dd-MM-yyyy"
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => applyDateFilter("expiry_date")}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <DatePicker
                        selected={formInputs.pay_date_start}
                        onChange={handlePayDateChange}
                        startDate={formInputs.pay_date_start}
                        endDate={formInputs.pay_date_end}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText=""
                        monthsShown={3}
                        openToDate={formInputs.pay_date_start || new Date()}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                        minDate={new Date(2018, 0, 1)}
                        maxDate={new Date(2030, 11, 31)}
                        dateFormat="dd-MM-yyyy"
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={() => applyDateFilter("pay_date")}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th>
                    <input
                      type="text"
                      name="trades_total"
                      placeholder="Total"
                      value={formInputs.trades_total}
                      onChange={handleFormInputChange}
                      onKeyDown={handleKeyDown}
                      style={{ width: "80px", padding: "5px" }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="trades_used"
                      placeholder="Used"
                      value={formInputs.trades_used}
                      onChange={handleFormInputChange}
                      onKeyDown={handleKeyDown}
                      style={{ width: "80px", padding: "5px" }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="trades_available"
                      placeholder="Available"
                      value={formInputs.trades_available}
                      onChange={handleFormInputChange}
                      onKeyDown={handleKeyDown}
                      style={{ width: "80px", padding: "5px" }}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {(loading || isRendering) ? (
                  // Skeleton loading rows
                  Array.from({ length: 10 }).map((_, skeletonIndex) => (
                    <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                      <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "75%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "65%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "70%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "60%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "80%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "65%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "40%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "40%" }}></div></td>
                      <td><div className="skeleton-text" style={{ width: "40%" }}></div></td>
                    </tr>
                  ))
                ) : subscriptionData.length > 0 ? (
                  subscriptionData.map((item, idx) => (
                    <tr key={idx}>
                      <td>{formatDateTime(item.created_on)}</td>
                      <td>{item.lead_source}</td>
                      <td>{item.service_name}</td>
                      <td>{item.stype}</td>
                      <td>{item.status}</td>
                      <td>{item.servicetype}</td>
                      <td>₹{item.amount}</td>
                      <td>
                        {formatDateWithTime(
                          item.activation_date,
                          item.activation_time
                        )}
                      </td>
                      <td>
                        {formatDateWithTime(item.expiry_date, item.expiry_time)}
                      </td>
                      <td>{formatDate(item.pay_date)}</td>
                      <td style={{ textAlign: "center" }}>{item.trades_total}</td>
                      <td style={{ textAlign: "center" }}>{item.trades_used}</td>
                      <td style={{ textAlign: "center" }}>{item.trades_available}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="13" className="text-center py-4">
                      No subscription records found
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan="7" style={{ textAlign: "left" }}>
                    {loading ? (
                      <span
                        className="skeleton-text"
                        style={{ width: "140px", display: "inline-block" }}
                      ></span>
                    ) : (
                      pagination.showingRecords
                    )}
                  </td>

                  <td style={{ textAlign: "left", fontWeight: "bold" }}>
                    {loading ? (
                      <span
                        className="skeleton-text"
                        style={{ width: "160px", display: "inline-block" }}
                      ></span>
                    ) : (
                      <>Total Amount: ₹{subscriptionAmount.amount || 0}</>
                    )}
                  </td>

                  <td colSpan="5">&nbsp;</td>
                </tr>
              </tfoot>

            </table>
          </div>
        </div>
      </div>

      <style jsx>{`
        .breadcrumb {
          background: transparent;
          margin-bottom: 0;
          padding: 0;
        }

        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
        }

        .panel {
          background: white;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .with-rounded-corner {
          border-radius: 4px;
        }

        .with-shadow {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .table-condensed th,
        .table-condensed td {
          padding: 8px 5px;
          font-size: 12px;
        }

        .btn-default {
          background: #f8f9fa;
          border: 1px solid #ddd;
        }

        .btn-xs {
          padding: 1px 5px;
          font-size: 12px;
          line-height: 1.5;
        }

        .form-control.input-sm {
          height: 28px;
          padding: 3px 6px;
          font-size: 12px;
        }

        th {
          white-space: nowrap;
          background: #f8f9fa;
        }

        .table-hover tbody tr:hover {
          background-color: #f5f5f5;
        }

        .badge {
          font-size: 0.7em;
        }
        .react-datepicker {
          margin-left: 190px;
        }
      `}</style>
    </div>
  );
};

export default SubscriptionAnalysisReport;
